/**
 * Performance arithmetic. Pure and isomorphic so the facet, the server routes, the agent tools and the
 * web all compute the same numbers. Every derived value names its rule and version; missing inputs
 * give a reason, never a zero or a guessed default.
 */
import { convertUnit, daysBetween, type FacetEvent, type Quantity } from "../../../contract";
import type {
  AnimalInputs, Calculated, CalcInputs, CarcassData, FormulaId, Gain, IntakeData, MeasureData, MilestoneWeight, Milestone, MissingReason,
  PerformanceSettings, PerformanceState, Scores, UltrasoundData, WeightData, WeightPoint,
} from "./types";

// ---------------------------------------------------------------- rules

export const RULES = {
  /** BIF: weaning weights are adjusted for calves weaned between 160 and 250 days of age. */
  weaningAge: [160, 250] as const,
  /** BIF: yearling weights between 320 and 410 days of age. */
  yearlingAge: [320, 410] as const,
  /** Hondo guard: a yearling gain needs at least this many days after weaning. */
  minPostWeaningDays: 60,
  /** BIF frame score equations cover 5 to 21 months of age. */
  frameAge: [152, 639] as const,
  /** Contemporary groups: calves born within this many days of the first calf in their window. */
  birthWindowDays: 90,
  /** MPPA repeatability. */
  repeatability: 0.4,
  /** Default threshold for "check this weight". */
  outlierPct: 15,
  /** Gains above this many pounds a day are not believable on a ranch scale. */
  maxBelievableGainLb: 4.5,
  /** Gains between weights closer than this are mostly gut fill, so ADG is not shown. */
  minAdgDays: 7,
} as const;

export const FORMULAS: Record<FormulaId, { version: number; label: string; text: string }> = {
  bif_205: { version: 1, label: "BIF adjusted 205-day weaning weight", text: "(weaning weight − birth weight) ÷ age in days × 205 + birth weight + age-of-dam adjustment" },
  bif_205_age: { version: 1, label: "205-day weight, age only", text: "(weaning weight − birth weight) ÷ age in days × 205 + birth weight, without the age-of-dam adjustment" },
  bif_365: { version: 1, label: "BIF adjusted 365-day yearling weight", text: "(yearling weight − weaning weight) ÷ days between × 160 + adjusted 205-day weight" },
  bif_365_age: { version: 1, label: "365-day weight, age only", text: "(yearling weight − weaning weight) ÷ days between × 160 + 205-day weight without the age-of-dam adjustment" },
  bif_frame: { version: 1, label: "BIF frame score", text: "BIF frame score equation for bulls or heifers, from hip height in inches and age in days" },
};

export const RATIO_TEXT = "100 × adjusted weight ÷ average adjusted weight of its contemporary group";
export const MPPA_TEXT = "100 + (N × 0.4 ÷ (1 + (N − 1) × 0.4)) × (average weaning ratio − 100)";

export const MISSING_TEXT: Record<MissingReason, string> = {
  birth_date: "Needs the calf's birth date",
  birth_weight: "Needs a birth weight",
  dam_age: "Needs the dam's birth date for the age-of-dam adjustment",
  sex: "Needs the calf's sex",
  age_range: "Weighed outside the age range the rule allows",
  estimate: "Visual estimates are not adjusted",
  date: "Needs the exact weighing date",
  weaning: "Needs an adjusted weaning weight first",
  too_close: "Weaning and yearling weights are less than 60 days apart",
  steer: "Frame score equations are for bulls and heifers",
};

/** BIF standard additive age-of-dam adjustments for 205-day weight, in pounds. */
export function aodAdjustment(damAgeYears: number, sex: "male" | "female"): number {
  const male = sex === "male";
  if (damAgeYears <= 2) return male ? 60 : 54;
  if (damAgeYears === 3) return male ? 40 : 36;
  if (damAgeYears === 4) return male ? 20 : 18;
  if (damAgeYears <= 10) return 0;
  return male ? 20 : 18;
}

/** Dam's age in whole years when the calf was born, or null when the dates don't make sense. */
export function damAgeYears(damBirth: string | null | undefined, calfBirth: string | null | undefined): number | null {
  const dam = isoDay(damBirth);
  const calf = isoDay(calfBirth);
  if (!dam || !calf) return null;
  const years = Math.round(daysBetween(dam, calf) / 365.25);
  if (years < 1) return null;
  return Math.max(2, years);
}

export function adjusted205(birthLb: number, weaningLb: number, ageDays: number, aodLb = 0): number {
  return round1(((weaningLb - birthLb) / ageDays) * 205 + birthLb + aodLb);
}

export function adjusted365(yearlingLb: number, weaningLb: number, daysBetweenWeighings: number, adjusted205Lb: number): number {
  return round1(((yearlingLb - weaningLb) / daysBetweenWeighings) * 160 + adjusted205Lb);
}

/** BIF frame score from hip height (inches) and age (days). */
export function frameScore(hipIn: number, ageDays: number, sex: "bull" | "heifer"): number {
  const a = ageDays;
  const h = hipIn;
  const v = sex === "bull"
    ? -11.548 + 0.4878 * h - 0.0289 * a + 0.00001947 * a * a + 0.0000334 * h * a
    : -11.7086 + 0.4723 * h - 0.0239 * a + 0.0000146 * a * a + 0.0000759 * h * a;
  return round1(v);
}

/** Ratios within one group: 100 × value ÷ mean. One animal alone has no comparison. */
export function ratios(values: number[]): { mean: number; ratios: number[] } | null {
  if (values.length < 2) return null;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  return { mean: round1(mean), ratios: values.map((v) => round1((100 * v) / mean)) };
}

/** Most probable producing ability from a dam's calf ratios. */
export function mppa(calfRatios: number[], r: number = RULES.repeatability): number | null {
  const n = calfRatios.length;
  if (!n) return null;
  const c = calfRatios.reduce((s, v) => s + v, 0) / n;
  return round1(100 + ((n * r) / (1 + (n - 1) * r)) * (c - 100));
}

/** Split birth dates into windows no wider than `span` days, oldest first. */
export function birthWindows<T extends { birth_date: string }>(items: T[], span: number = RULES.birthWindowDays): T[][] {
  const sorted = [...items].sort((a, b) => a.birth_date.localeCompare(b.birth_date));
  const out: T[][] = [];
  let start: string | null = null;
  for (const item of sorted) {
    if (!start || daysBetween(start, item.birth_date) > span) { out.push([]); start = item.birth_date; }
    out[out.length - 1].push(item);
  }
  return out;
}

// ---------------------------------------------------------------- weights and gain

export function toLb(q: Quantity | null | undefined): number | null {
  if (!q || typeof q.value !== "number") return null;
  return convertUnit(q.value, q.unit, "lb");
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** YYYY-MM-DD for a stored date of any precision, or null when unknown. */
export function isoDay(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  if (/^\d{4}-\d{2}$/.test(value)) return `${value}-15`;
  if (/^\d{4}$/.test(value)) return `${value}-07-01`;
  return null;
}

/** Average daily gain between two weights, in the later weight's unit. Null when the dates don't allow it. */
export function gainBetween(from: { value: number; unit: string; on: string }, to: { value: number; unit: string; on: string }): Gain | null {
  const days = daysBetween(from.on, to.on);
  if (days < 1) return null;
  const metric = to.unit === "kg";
  const a = convertUnit(from.value, from.unit, metric ? "kg" : "lb");
  const b = convertUnit(to.value, to.unit, metric ? "kg" : "lb");
  if (a === null || b === null) return null;
  return { value: round2((b - a) / days), unit: metric ? "kg_per_day" : "lb_per_day", from: from.on, to: to.on, days };
}

export interface WeightCheck { kind: "drop" | "jump" | "same_day"; pct: number; days: number; adg_lb: number | null; previous: { value: number; unit: string; on: string } }

/**
 * Should someone look at this weight before trusting it? Flags a drop beyond the threshold, a change
 * beyond the threshold within two days (likely a misread or wrong animal), or a gain no ranch scale
 * would believe. Normal calf growth over months is never flagged.
 */
export function checkWeight(previous: { value: number; unit: string; on: string } | null | undefined, next: { value: number; unit: string; on: string }, thresholdPct: number = RULES.outlierPct): WeightCheck | null {
  if (!previous) return null;
  const a = convertUnit(previous.value, previous.unit, "lb");
  const b = convertUnit(next.value, next.unit, "lb");
  if (a === null || b === null || a <= 0) return null;
  const pct = round1(((b - a) / a) * 100);
  const days = daysBetween(previous.on, next.on);
  const adg = days >= 1 ? round2((b - a) / days) : null;
  const base = { pct, days, adg_lb: adg, previous };
  if (Math.abs(pct) <= thresholdPct) return null;
  if (days <= 2) return { kind: "same_day", ...base };
  if (pct < 0) return { kind: "drop", ...base };
  if (adg !== null && adg > RULES.maxBelievableGainLb) return { kind: "jump", ...base };
  return null;
}

/** The latest actual (non-estimate) weight on or before a date, for checks and gain. */
export function weightBefore(history: WeightPoint[], on: string, excludeEventId?: string): WeightPoint | null {
  let best: WeightPoint | null = null;
  for (const p of history) {
    if (p.event_id === excludeEventId || p.estimate) continue;
    if (p.on <= on && (!best || p.on >= best.on)) best = p;
  }
  return best;
}

// ---------------------------------------------------------------- the facet reducer

const SCORE_KEYS = ["docility", "udder", "teat", "foot_angle", "claw_set", "muscle", "frame", "pelvic_area"] as const;

function eventDay(e: FacetEvent): { on: string; approx: boolean } | null {
  const on = isoDay(e.occurred_at);
  if (!on) return null;
  const approx = !["date", "minute", "estimated_date", "date_range"].includes(e.occurred_precision);
  return { on, approx };
}

/** Birth weight for this calf from a calving record, read defensively (the breeding module owns calving). */
function calvingBirthWeight(e: FacetEvent, animalId: string | undefined): Quantity | null {
  if (!e.roles.includes("calf")) return null;
  const calves = (e.data as { calves?: Array<Record<string, unknown>> }).calves;
  if (!Array.isArray(calves)) return null;
  const mine = calves.find((c) => c && (c.calf_id === animalId || c.animal_id === animalId)) ?? (calves.length === 1 ? calves[0] : undefined);
  const w = (mine?.weight ?? mine?.birth_weight) as Quantity | undefined;
  return w && typeof w.value === "number" && typeof w.unit === "string" ? w : null;
}

/** Pure reducer for the animal `performance` facet. Events arrive oldest first. */
export function reducePerformance(events: FacetEvent[]): PerformanceState | null {
  const state: PerformanceState = { weights: {}, scores: {}, history: [], weighed: 0 };
  let touched = false;
  const animalId = (e: FacetEvent) => e.subjects.find((s) => s.kind === "animal" && e.roles.includes(s.role))?.id;
  for (const e of events) {
    const day = eventDay(e);
    if (e.type === "performance.weight") {
      const d = e.data as WeightData;
      touched = true;
      if (d.adjusted_weight && d.context && (d.context === "birth" || d.context === "weaning" || d.context === "yearling")) {
        state.imported = { ...state.imported, [d.context]: { value: d.adjusted_weight.value, unit: d.adjusted_weight.unit, on: day?.on ?? null, by: d.adjusted_by, ratio: d.ratio, event_id: e.id } };
      }
      if (!d.weight || !day) continue;
      const estimate = d.method === "estimate";
      state.weighed++;
      state.history.push({ value: d.weight.value, unit: d.weight.unit, on: day.on, event_id: e.id, context: d.context, ...(estimate ? { estimate } : {}), job_id: e.job_id });
      if (d.context === "birth" || d.context === "weaning" || d.context === "yearling") {
        state.weights[d.context] = {
          value: d.weight.value, unit: d.weight.unit, on: day.on, event_id: e.id, source: "weight", job_id: e.job_id,
          ...(d.method ? { method: d.method } : {}), ...(d.group_code ? { group_code: d.group_code } : {}), ...(day.approx ? { approx_date: true } : {}),
        };
      }
    } else if (e.type === "performance.measurement" && day) {
      const d = e.data as MeasureData;
      touched = true;
      if (typeof d.bcs === "number") state.bcs = { score: d.bcs, on: day.on, event_id: e.id };
      if (d.scrotal) state.scores.scrotal = { ...d.scrotal, on: day.on, event_id: e.id };
      if (d.hip_height) state.scores.hip_height = { ...d.hip_height, on: day.on, event_id: e.id };
      for (const key of SCORE_KEYS) {
        const raw = d[key];
        if (raw === undefined || raw === null) continue;
        const value = Number(raw);
        if (!Number.isNaN(value)) (state.scores as Record<string, unknown>)[key] = { value, on: day.on, event_id: e.id };
      }
    } else if (e.type === "performance.ultrasound" && day) {
      const d = e.data as UltrasoundData;
      touched = true;
      state.ultrasound = {
        on: day.on, event_id: e.id, ...(d.imf !== undefined ? { imf: d.imf } : {}), ...(d.rea !== undefined && d.rea_unit ? { rea: { value: d.rea, unit: d.rea_unit } } : {}),
        ...(d.rib_fat ? { rib_fat: d.rib_fat } : {}), ...(d.rump_fat ? { rump_fat: d.rump_fat } : {}), ...(d.scan_weight ? { scan_weight: d.scan_weight } : {}),
        ...(d.imf_ratio !== undefined ? { imf_ratio: d.imf_ratio } : {}), ...(d.rea_ratio !== undefined ? { rea_ratio: d.rea_ratio } : {}), ...(d.fat_ratio !== undefined ? { fat_ratio: d.fat_ratio } : {}),
      };
    } else if (e.type === "performance.carcass" && day) {
      const d = e.data as CarcassData;
      touched = true;
      state.carcass = {
        on: day.on, event_id: e.id, ...(d.hcw ? { hcw: d.hcw } : {}), ...(d.quality_grade ? { quality_grade: d.quality_grade } : {}),
        ...(d.yield_grade !== undefined ? { yield_grade: d.yield_grade } : {}), ...(d.marbling ? { marbling: d.marbling } : {}),
        ...(d.rea !== undefined && d.rea_unit ? { rea: { value: d.rea, unit: d.rea_unit } } : {}), ...(d.back_fat ? { back_fat: d.back_fat } : {}), ...(d.plant ? { plant: d.plant } : {}),
      };
    } else if (e.type === "breeding.calving" && day) {
      const w = calvingBirthWeight(e, animalId(e));
      if (!w) continue;
      touched = true;
      // A birth weight recorded as a weight wins over the one on the calving record.
      if (!state.weights.birth || state.weights.birth.source === "calving") {
        state.weights.birth = { value: w.value, unit: w.unit, on: day.on, event_id: e.id, source: "calving" };
      }
      if (!state.history.some((p) => p.event_id === e.id)) {
        state.history.push({ value: w.value, unit: w.unit, on: day.on, event_id: e.id, context: "birth" });
        state.weighed++;
      }
    }
  }
  if (!touched) return null;
  state.history.sort((a, b) => a.on.localeCompare(b.on));
  if (state.history.length > 120) state.history = state.history.slice(-120);
  const last = state.history.at(-1);
  if (last) state.last_weight = { value: last.value, unit: last.unit, on: last.on, event_id: last.event_id, ...(last.context ? { context: last.context } : {}), ...(last.estimate ? { estimate: true } : {}) };
  const actual = state.history.filter((p) => !p.estimate);
  const latest = actual.at(-1);
  if (latest) {
    const earlier = [...actual].reverse().find((p) => daysBetween(p.on, latest.on) >= RULES.minAdgDays);
    const gain = earlier ? gainBetween(earlier, latest) : null;
    if (gain) state.adg = gain;
    const birth = state.weights.birth;
    const sinceBirth = birth && birth.event_id !== latest.event_id ? gainBetween(birth, latest) : null;
    if (sinceBirth && sinceBirth.days >= RULES.minAdgDays) state.adg_birth = sinceBirth;
  }
  return state;
}

// ---------------------------------------------------------------- adjusted values from the animal's record

function calc(formula: FormulaId, value: number, unit: Calculated["unit"], inputs: CalcInputs, today: string, ageDays?: number): Calculated {
  return { value, unit, basis: "calculated", formula, version: FORMULAS[formula].version, ...(ageDays !== undefined ? { age_days: ageDays } : {}), inputs, computed_on: today };
}

/**
 * Adds adjusted weights and calculated frame score to a reduced state, using the animal's birth date,
 * sex and dam. Returns a new state; the input is not changed.
 */
export function deriveAdjusted(state: PerformanceState, animal: AnimalInputs, settings: PerformanceSettings, today: string): PerformanceState {
  const out: PerformanceState = structuredClone(state);
  const birthDate = isoDay(animal.birth_date) ?? (out.weights.birth && !out.weights.birth.approx_date ? out.weights.birth.on : null);
  const sex = animal.sex === "male" ? "male" : animal.sex === "female" ? "female" : null;

  // Birth weight: recorded, or the ranch's stated assumption, never a silent default.
  const recordedBirth = out.weights.birth;
  const assumed = settings.assumed_birth_weight;
  const birthInput: CalcInputs["birth_weight"] | null = recordedBirth
    ? { value: recordedBirth.value, unit: recordedBirth.unit, on: recordedBirth.on, event_id: recordedBirth.event_id }
    : assumed && typeof assumed.value === "number" ? { value: assumed.value, unit: assumed.unit, assumed: true } : null;
  const birthLb = birthInput ? toLb(birthInput) : null;

  const wean = out.weights.weaning;
  if (wean) {
    delete wean.adjusted; delete wean.age_adjusted; delete wean.missing;
    const missing: MissingReason[] = [];
    const weanLb = toLb(wean);
    const age = birthDate ? daysBetween(birthDate, wean.on) : null;
    if (wean.method === "estimate") missing.push("estimate");
    if (wean.approx_date) missing.push("date");
    if (age === null) missing.push("birth_date");
    else if (age < RULES.weaningAge[0] || age > RULES.weaningAge[1]) missing.push("age_range");
    if (birthLb === null) missing.push("birth_weight");
    if (!missing.length && weanLb !== null && age !== null && birthLb !== null && birthInput) {
      const inputs: CalcInputs = { birth_weight: birthInput, weaning_weight: { value: wean.value, unit: wean.unit, on: wean.on, event_id: wean.event_id }, birth_date: birthDate!, age_days: age };
      wean.age_adjusted = calc("bif_205_age", adjusted205(birthLb, weanLb, age), "lb", inputs, today, age);
      const damAge = damAgeYears(animal.dam_birth_date, birthDate);
      if (!sex) missing.push("sex");
      if (damAge === null) missing.push("dam_age");
      if (sex && damAge !== null) {
        const aod = aodAdjustment(damAge, sex);
        wean.adjusted = calc("bif_205", adjusted205(birthLb, weanLb, age, aod), "lb", { ...inputs, dam_age_years: damAge, aod_lb: aod, sex }, today, age);
      }
    }
    if (missing.length) wean.missing = missing;
  }

  const year = out.weights.yearling;
  if (year) {
    delete year.adjusted; delete year.age_adjusted; delete year.missing;
    const missing: MissingReason[] = [];
    const yearLb = toLb(year);
    const age = birthDate ? daysBetween(birthDate, year.on) : null;
    if (year.method === "estimate") missing.push("estimate");
    if (year.approx_date) missing.push("date");
    if (age === null) missing.push("birth_date");
    else if (age < RULES.yearlingAge[0] || age > RULES.yearlingAge[1]) missing.push("age_range");
    const base = wean?.adjusted ?? wean?.age_adjusted;
    if (!wean || !base) missing.push("weaning");
    const gap = wean ? daysBetween(wean.on, year.on) : 0;
    if (wean && base && gap < RULES.minPostWeaningDays) missing.push("too_close");
    const weanLb = wean ? toLb(wean) : null;
    if (!missing.length && yearLb !== null && weanLb !== null && wean && age !== null) {
      const common: CalcInputs = {
        weaning_weight: { value: wean.value, unit: wean.unit, on: wean.on, event_id: wean.event_id },
        yearling_weight: { value: year.value, unit: year.unit, on: year.on, event_id: year.event_id }, days_between: gap, age_days: age, birth_date: birthDate!,
      };
      if (wean.age_adjusted) year.age_adjusted = calc("bif_365_age", adjusted365(yearLb, weanLb, gap, wean.age_adjusted.value), "lb", { ...common, weaning_adjusted: wean.age_adjusted.value }, today, age);
      if (wean.adjusted) year.adjusted = calc("bif_365", adjusted365(yearLb, weanLb, gap, wean.adjusted.value), "lb", { ...common, weaning_adjusted: wean.adjusted.value }, today, age);
      else missing.push(...(wean.missing ?? ["weaning" as const]).filter((m) => m === "dam_age" || m === "sex"));
    }
    if (missing.length) year.missing = [...new Set(missing)];
  }

  delete out.scores.frame_calc;
  const hip = out.scores.hip_height;
  if (hip && birthDate) {
    const inches = convertUnit(hip.value, hip.unit, "in");
    const age = daysBetween(birthDate, hip.on);
    const frameSex = animal.sex === "male" && !animal.castrated ? "bull" : animal.sex === "female" ? "heifer" : null;
    if (inches !== null && frameSex && age >= RULES.frameAge[0] && age <= RULES.frameAge[1]) {
      out.scores.frame_calc = calc("bif_frame", frameScore(inches, age, frameSex), "score", { hip_height_in: round1(inches), age_days: age, frame_sex: frameSex, birth_date: birthDate }, today, age);
    }
  }
  out.computed = { on: today, version: 1 };
  return out;
}

/** Projected weight today from the last actual weight and recent gain. Clearly a projection, never a weight. */
export function projectWeight(state: PerformanceState | null | undefined, today: string): { value: number; unit: string; on: string; from: string; adg: number } | null {
  if (!state?.last_weight || state.last_weight.estimate || !state.adg || state.adg.value <= 0) return null;
  const days = daysBetween(state.last_weight.on, today);
  if (days < RULES.minAdgDays || days > 120) return null;
  const rate = state.adg.unit === "kg_per_day" ? convertUnit(state.adg.value, "kg", state.last_weight.unit) : convertUnit(state.adg.value, "lb", state.last_weight.unit);
  if (rate === null) return null;
  return { value: Math.round(state.last_weight.value + rate * days), unit: state.last_weight.unit, on: today, from: state.last_weight.on, adg: state.adg.value };
}

// ---------------------------------------------------------------- gain tests

export interface TestWeight { value: number; unit: string; on: string; event_id: string }
export interface TestIntake { as_fed_lb?: number; dry_matter_lb?: number; days: number; basis: IntakeData["basis"]; event_id: string; from: string; to: string }

export interface TestResult {
  start?: TestWeight;
  latest?: TestWeight;
  weighings: number;
  days?: number;
  adg?: number;            // lb/day
  gain_lb?: number;
  intake?: { as_fed_lb?: number; dry_matter_lb?: number; days: number; per_day_lb?: number; basis: IntakeData["basis"]; coverage_pct?: number };
  feed_to_gain?: number;   // lb feed per lb gain, on the intake basis reported
  dm_to_gain?: number;
  problem?: "no_start" | "one_weight" | "same_day";
}

/** One animal's test result from its own test weights (oldest first) and intake records. */
export function testResult(weights: TestWeight[], intake: TestIntake[]): TestResult {
  const sorted = [...weights].sort((a, b) => a.on.localeCompare(b.on));
  const out: TestResult = { weighings: sorted.length };
  if (!sorted.length) return { ...out, problem: "no_start" };
  out.start = sorted[0];
  out.latest = sorted.at(-1);
  if (sorted.length === 1) return { ...out, problem: "one_weight" };
  const days = daysBetween(out.start.on, out.latest!.on);
  if (days < 1) return { ...out, problem: "same_day" };
  const a = toLb(out.start)!;
  const b = toLb(out.latest!)!;
  out.days = days;
  out.gain_lb = round1(b - a);
  out.adg = round2((b - a) / days);
  const inWindow = intake.filter((i) => i.from >= out.start!.on && i.to <= out.latest!.on);
  if (inWindow.length) {
    const asFed = inWindow.every((i) => i.as_fed_lb !== undefined) ? inWindow.reduce((s, i) => s + i.as_fed_lb!, 0) : undefined;
    const dm = inWindow.every((i) => i.dry_matter_lb !== undefined) ? inWindow.reduce((s, i) => s + i.dry_matter_lb!, 0) : undefined;
    const intakeDays = inWindow.reduce((s, i) => s + i.days, 0);
    const basis = inWindow.some((i) => i.basis === "allocated") ? "allocated" : "measured";
    const total = dm ?? asFed;
    out.intake = {
      ...(asFed !== undefined ? { as_fed_lb: round1(asFed) } : {}), ...(dm !== undefined ? { dry_matter_lb: round1(dm) } : {}), days: intakeDays, basis,
      ...(total !== undefined && intakeDays ? { per_day_lb: round1(total / intakeDays) } : {}), coverage_pct: Math.min(100, Math.round((intakeDays / days) * 100)),
    };
    // Feed per pound of gain only over a positive gain and when intake covers the whole test.
    if (out.gain_lb > 0 && intakeDays >= days) {
      if (asFed !== undefined) out.feed_to_gain = round2(asFed / out.gain_lb);
      if (dm !== undefined) out.dm_to_gain = round2(dm / out.gain_lb);
    }
  }
  return out;
}

export function intakeDays(from: string, to: string | null | undefined, days?: number): number {
  if (typeof days === "number" && days > 0) return days;
  if (to) return daysBetween(from, to) + 1;
  return 1;
}

export type { MilestoneWeight, Milestone, Scores, Calculated };
